import { SplitComponent } from 'angular-split';
import { effectiveZoom } from './effective-zoom';

/**
 * angular-split gutter drags under the density zoom (GEN-E-100-06).
 *
 * A `unit="pixel"` split turns a gutter drag into area sizes by taking the pointer's `clientX/Y`
 * delta (screen px) and writing it straight into its areas' px sizes (local px, which the zoom then
 * scales again). So at compact a 100px drag moved the gutter 85px and it fell behind the cursor —
 * the XatBot shell, missions, mission workspace, tax-reviews document, show workspace, the ITP
 * platform-tool layout and the dashboard XatBot widget all split this way. A `unit="percent"` split
 * is already right: it divides the same screen-px delta by the split's `getBoundingClientRect()`.
 *
 * angular-split exposes no scale input, so this wraps its drag step once: for a pointer drag on a
 * pixel split, the end point is moved back towards the start by the split's effective zoom, so the
 * delta it applies is in local px. Keyboard moves are left alone — they step from the gutter's
 * `offsetLeft`, which is already local. At comfortable the zoom is 1 and nothing changes.
 */

export interface SplitPoint {
  x: number;
  y: number;
}

interface SplitDragContext {
  startEvent: MouseEvent | TouchEvent | KeyboardEvent;
}

interface ZoomableSplit {
  unit(): 'percent' | 'pixel';
  elementRef?: { nativeElement: Element };
  dragMoveToPoint(endPoint: SplitPoint, dragStartContext: SplitDragContext): void;
}

let installed = false;

/** The screen-px point angular-split reads from a pointer event, or undefined for any other event. */
export function pointerStartPoint(event: Event | undefined): SplitPoint | undefined {
  if (!event) {
    return undefined;
  }
  if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent) {
    const touch = event.changedTouches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : undefined;
  }
  if (event instanceof MouseEvent) {
    return { x: event.clientX, y: event.clientY };
  }
  return undefined;
}

/** `end` with its screen-px distance from `start` converted to local px at `zoom`. */
export function zoomAdjustedEndPoint(start: SplitPoint, end: SplitPoint, zoom: number): SplitPoint {
  if (zoom === 1 || !(zoom > 0) || !Number.isFinite(zoom)) {
    return end;
  }
  return {
    x: start.x + (end.x - start.x) / zoom,
    y: start.y + (end.y - start.y) / zoom
  };
}

/**
 * Installs the correction on angular-split's `SplitComponent` once. Returns false (and leaves the
 * library alone) if the drag step it wraps is missing — the spec pins it, so an angular-split upgrade
 * that renames it fails a test rather than silently dropping the fix.
 */
export function installSplitPointerZoomFix(proto: ZoomableSplit = SplitComponent.prototype as unknown as ZoomableSplit): boolean {
  if (installed && proto === (SplitComponent.prototype as unknown as ZoomableSplit)) {
    return true;
  }
  const original = proto.dragMoveToPoint;
  if (typeof original !== 'function') {
    return false;
  }
  proto.dragMoveToPoint = function (this: ZoomableSplit, endPoint: SplitPoint, dragStartContext: SplitDragContext) {
    const start = this.unit() === 'pixel' ? pointerStartPoint(dragStartContext?.startEvent) : undefined;
    const host = this.elementRef?.nativeElement;
    const end = start && endPoint && host ? zoomAdjustedEndPoint(start, endPoint, effectiveZoom(host)) : endPoint;
    return original.call(this, end, dragStartContext);
  };
  if (proto === (SplitComponent.prototype as unknown as ZoomableSplit)) {
    installed = true;
  }
  return true;
}
